import { useCurrentCompany } from "hooks/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import { getAddendumServices } from "services/supplier-flow/adendumServices";

import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";

import AddendumsComp from "../RFQPreview/AddendumsComp";
import type { Addendum } from "./Addendum.types";

type AddendumContainerProps = {
	disabled: boolean;
	guestEmail: string;
	isCommunication: boolean;
	isGuest: boolean;
	rfqId: number;
};

export const AddendumContainer = ({
	isCommunication,
	rfqId,
	isGuest,
	guestEmail,
	disabled,
}: AddendumContainerProps) => {
	const [addendums, setAddendums] = useState<Addendum[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const company = useCurrentCompany();
	const { rfxData, updateLocalRfxData } = useRfxSubmissionData();

	// Use a ref for rfxData so the callback identity stays stable and doesn't
	// re-trigger the fetch useEffect, which previously caused an infinite loop.
	const rfxDataRef = useRef(rfxData);
	rfxDataRef.current = rfxData;

	const setAddendumData = useCallback((addendumData) => {
		const totalQuestions = Array.isArray(addendumData)
			? addendumData.reduce((sum, item) => {
				return sum + (item.question?.length ?? 0);
			}, 0)
			: 0;

		const current = rfxDataRef.current;
		updateLocalRfxData(
			isCommunication
				? ({ ...current, comms_count: totalQuestions } as Rfx)
				: ({ ...current, addendum_count: totalQuestions } as Rfx)
		);

		setAddendums(addendumData);
	}, [isCommunication, updateLocalRfxData]);

	// Keep the latest setAddendumData in a ref so the fetch effect does not
	// depend on its identity. setAddendumData -> updateLocalRfxData mutates the
	// parent Rfx context, whose value object is rebuilt each render; that churns
	// updateLocalRfxData (and thus setAddendumData) identity. Depending on it
	// here re-fired the fetch on every parent render — the "constantly
	// refreshing" addendum tab (PRO-531 regression).
	const setAddendumDataRef = useRef(setAddendumData);
	setAddendumDataRef.current = setAddendumData;

	useEffect(() => {
		setIsLoading(true);

		getAddendumServices(
			rfqId,
			isCommunication ? "unpublished" : "published"
		).then((res) => {
			setAddendumDataRef.current(res?.data?.data);
			setIsLoading(false);
		});
	}, [rfqId, isCommunication]);

	// const updateTotals = (data)

	return (
		// @ts-ignore
		<AddendumsComp
			rfqId={rfqId}
			addendumData={addendums}
			setAddendumData={setAddendumData}
			isGuest={isGuest}
			guestEmail={guestEmail}
			disabled={disabled}
			isCommunication={isCommunication}
			isDataLoading={isLoading}
			company={company}
		/>
	);
};
